/**
 * Test Helpers for Context Path System
 *
 * These utilities help verify that the context path system works correctly.
 * Can be run in the browser console for quick verification.
 */

import {
  createContextPath,
  extendPath,
  extractLegacyParams,
  getCurrentContainerId,
  getCurrentContainerType,
  getDepth,
  pathToString,
  isParentPath
} from './contextPath';

/**
 * Test Suite 1: Basic Path Operations
 */
export const testBasicPaths = () => {
  console.group('🧪 Test Suite 1: Basic Path Operations');

  // Test 1.1: Main table only
  const test1 = createContextPath('table-1');
  const result1 = extractLegacyParams(test1);
  console.assert(
    result1.tableId === 'table-1' &&
    result1.subtableId === null &&
    result1.nestedTableId === null,
    '❌ Test 1.1 Failed: Main table only',
    { expected: { tableId: 'table-1', subtableId: null, nestedTableId: null }, actual: result1 }
  );
  console.log('✅ Test 1.1 Passed: Main table only', result1);

  // Test 1.2: Main → Subtable
  const test2 = extendPath(test1, { type: 'subtable', id: 'sub-1' });
  const result2 = extractLegacyParams(test2);
  console.assert(
    result2.tableId === 'table-1' &&
    result2.subtableId === 'sub-1' &&
    result2.nestedTableId === null,
    '❌ Test 1.2 Failed: Main → Subtable',
    { expected: { tableId: 'table-1', subtableId: 'sub-1', nestedTableId: null }, actual: result2 }
  );
  console.log('✅ Test 1.2 Passed: Main → Subtable', result2);

  // Test 1.3: Main → Nested
  const test3 = extendPath(test1, { type: 'nested', id: 'nest-1' });
  const result3 = extractLegacyParams(test3);
  console.assert(
    result3.tableId === 'table-1' &&
    result3.subtableId === null &&
    result3.nestedTableId === 'nest-1',
    '❌ Test 1.3 Failed: Main → Nested',
    { expected: { tableId: 'table-1', subtableId: null, nestedTableId: 'nest-1' }, actual: result3 }
  );
  console.log('✅ Test 1.3 Passed: Main → Nested', result3);

  console.groupEnd();
};

/**
 * Test Suite 2: Deep Nesting
 */
export const testDeepNesting = () => {
  console.group('🧪 Test Suite 2: Deep Nesting');

  // Test 2.1: Main → Subtable → Nested
  let path = createContextPath('table-1');
  path = extendPath(path, { type: 'subtable', id: 'sub-1' });
  path = extendPath(path, { type: 'nested', id: 'nest-1' });

  const result1 = extractLegacyParams(path);
  console.assert(
    result1.nestedTableId === 'nest-1' && result1.subtableId === null,
    '❌ Test 2.1 Failed: Should return ONLY last segment (nested)',
    { expected: { nestedTableId: 'nest-1', subtableId: null }, actual: result1 }
  );
  console.log('✅ Test 2.1 Passed: Main → Subtable → Nested returns last segment only', result1);

  // Test 2.2: Main → Nested → Subtable → Nested → Subtable
  path = createContextPath('table-1');
  path = extendPath(path, { type: 'nested', id: 'nest-1' });
  path = extendPath(path, { type: 'subtable', id: 'sub-1' });
  path = extendPath(path, { type: 'nested', id: 'nest-2' });
  path = extendPath(path, { type: 'subtable', id: 'sub-2' });

  const result2 = extractLegacyParams(path);
  const depth = getDepth(path);
  const pathStr = pathToString(path);

  console.assert(
    result2.subtableId === 'sub-2' && result2.nestedTableId === null,
    '❌ Test 2.2 Failed: Should return last segment (subtable)',
    { expected: { subtableId: 'sub-2', nestedTableId: null }, actual: result2 }
  );
  console.log('✅ Test 2.2 Passed: Deep nesting returns last segment', { depth, pathStr, extracted: result2 });

  // Test 2.3: Depth calculation
  console.assert(
    depth === 4,
    '❌ Test 2.3 Failed: Depth should be 4',
    { expected: 4, actual: depth }
  );
  console.log('✅ Test 2.3 Passed: Depth calculation correct', depth);

  console.groupEnd();
};

/**
 * Test Suite 3: Path String Representation
 */
export const testPathStrings = () => {
  console.group('🧪 Test Suite 3: Path String Representation');

  let path = createContextPath('main-123');
  console.log('Level 0:', pathToString(path));

  path = extendPath(path, { type: 'nested', id: 'nest-456' });
  console.log('Level 1:', pathToString(path));

  path = extendPath(path, { type: 'subtable', id: 'sub-789' });
  console.log('Level 2:', pathToString(path));

  path = extendPath(path, { type: 'nested', id: 'nest-abc' });
  console.log('Level 3:', pathToString(path));

  console.assert(
    pathToString(path).includes('→'),
    '❌ Path string should contain arrows',
    pathToString(path)
  );
  console.log('✅ Test 3 Passed: Path strings generated correctly');

  console.groupEnd();
};

/**
 * Test Suite 4: Parent Path Operations
 */
export const testParentPaths = () => {
  console.group('🧪 Test Suite 4: Parent Path Operations');

  let path = createContextPath('table-1');
  path = extendPath(path, { type: 'nested', id: 'nest-1' });
  path = extendPath(path, { type: 'subtable', id: 'sub-1' });

  const parent = createContextPath('table-1');
  const parentWithNested = extendPath(parent, { type: 'nested', id: 'nest-1' });

  console.assert(
    isParentPath(parent, path),
    '❌ Test 4.1 Failed: Should recognize parent path',
    { parent: pathToString(parent), child: pathToString(path) }
  );
  console.log('✅ Test 4.1 Passed: Parent path recognized');

  console.assert(
    isParentPath(parentWithNested, path),
    '❌ Test 4.2 Failed: Should recognize immediate parent',
    { parent: pathToString(parentWithNested), child: pathToString(path) }
  );
  console.log('✅ Test 4.2 Passed: Immediate parent recognized');

  console.groupEnd();
};

/**
 * Run all tests
 */
export const runAllTests = () => {
  console.clear();
  console.log('🚀 Running Context Path System Tests...\n');

  try {
    testBasicPaths();
    testDeepNesting();
    testPathStrings();
    testParentPaths();

    console.log('\n✅ ALL TESTS PASSED! Context path system is working correctly.');
    return true;
  } catch (error) {
    console.error('\n❌ TESTS FAILED:', error);
    return false;
  }
};

/**
 * Validation helper: Check if extracted params are correct for adding to a container
 */
export const validateParentForAddition = (contextPath, expectedType, expectedId) => {
  const extracted = extractLegacyParams(contextPath);
  const depth = getDepth(contextPath);
  const pathStr = pathToString(contextPath);

  console.group('🔍 Validating Parent for Addition');
  console.log('Context Path:', pathStr);
  console.log('Depth:', depth);
  console.log('Extracted:', extracted);

  let isValid = false;

  if (expectedType === 'subtable') {
    isValid = extracted.subtableId === expectedId && extracted.nestedTableId === null;
  } else if (expectedType === 'nested') {
    isValid = extracted.nestedTableId === expectedId && extracted.subtableId === null;
  } else if (expectedType === 'main') {
    isValid = extracted.subtableId === null && extracted.nestedTableId === null;
  }

  if (isValid) {
    console.log('✅ Valid: Will add to correct parent');
  } else {
    console.error('❌ Invalid: Wrong parent will be used!');
    console.error('Expected:', { type: expectedType, id: expectedId });
    console.error('Actual:', extracted);
  }

  console.groupEnd();
  return isValid;
};

/**
 * Quick test for Main → Subtable → Nested scenario
 */
export const testCriticalScenario = () => {
  console.group('🎯 Critical Test: Main → Subtable → Add Nested');

  const mainPath = createContextPath('main-table-id');
  console.log('Step 1 - Main table:', pathToString(mainPath));

  const subtablePath = extendPath(mainPath, { type: 'subtable', id: 'subtable-123' });
  console.log('Step 2 - Added subtable:', pathToString(subtablePath));

  // This is the context when user clicks "Add Nested Table" in subtable cell
  const extracted = extractLegacyParams(subtablePath);
  console.log('Step 3 - Extracted params for API call:', extracted);

  // Verify: Should add nested table TO THE SUBTABLE
  const isCorrect = extracted.subtableId === 'subtable-123' && extracted.nestedTableId === null;

  if (isCorrect) {
    console.log('✅ CORRECT: Will call API with parent_subtable_id="subtable-123"');
    console.log('✅ Backend will add nested table INSIDE the subtable');
  } else {
    console.error('❌ WRONG: Extracted params are incorrect!');
    console.error('Expected: { subtableId: "subtable-123", nestedTableId: null }');
    console.error('Actual:', extracted);
  }

  console.groupEnd();
  return isCorrect;
};

// Export a window global for easy console access
if (typeof window !== 'undefined') {
  window.contextPathTests = {
    runAll: runAllTests,
    basic: testBasicPaths,
    deep: testDeepNesting,
    strings: testPathStrings,
    parents: testParentPaths,
    critical: testCriticalScenario,
    validate: validateParentForAddition
  };

  console.log('💡 Context Path Tests available via: window.contextPathTests');
  console.log('   Run all: window.contextPathTests.runAll()');
  console.log('   Critical test: window.contextPathTests.critical()');
}

export default {
  runAllTests,
  testBasicPaths,
  testDeepNesting,
  testPathStrings,
  testParentPaths,
  testCriticalScenario,
  validateParentForAddition
};
